import React, { memo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';
import styles from './EditSegmentTabs.module.css';
import EditSegmentTooltip from './CaptionTab/EditSegmentTooltip';
import { MediaItem, Caption } from '../../types';
import MediaTab from './MediaTab/MediaTab';
import LibraryTab from './LibraryTab';
import PreviewTab from './PreviewTab';
import { PromptBrowser } from '../shared';
import { PromptProject } from '../../services/projectService';
import { useNotification } from '../../context/NotificationContext';
import { useSearchParams } from 'react-router-dom';
import { PreviewOption } from '../../services/videoPreviewService';
import { handlePreviewSelection, handleClearPreview, handlePreviewSelectionSync } from '../VideoEditor/previewHandlers';
import { useTranslation } from 'react-i18next';
import { isAdminUser } from '../../config/environment';
import { useAuth } from '../../context/Auth/AuthContext';

interface ModalData {
  isOpen: boolean;
  onClose: () => void;
  segment?: { id: string };
  mediaItem?: MediaItem;
}

interface EditSegmentTabsProps {
  modalData: ModalData | null;
  captions: Caption[];
  setCaptions: React.Dispatch<React.SetStateAction<Caption[]>>;
  getCaptionId: (id: string, captions: Caption[]) => number;
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  mediaItems: MediaItem[];
  onOpenAIVideoModal?: (imageUrl: string, prompt?: string) => void;
  onOpenUpscaleModal?: (videoUrl: string) => void;
  projectId?: string; // Add projectId prop
  addAtCurrentTime?: boolean; // NEW: checkbox state
  currentTime?: number; // NEW: current timeline position
  editMode?: 'captions' | 'titles'; // NEW: edit mode prop
  applyToAll?: boolean; // NEW: receive applyToAll state from parent
  // NEW: Conversion props
  isConversionMode?: boolean;
  targetAspectRatio?: string | null;
  selectedMediaItem?: MediaItem | null;
  onResetMediaItem?: (itemId: string) => void;
  // NEW: Preview props
  allMediaItemsFromDB?: MediaItem[]; // Full DB data including preview segments
  initialMediaItems?: MediaItem[]; // Initial state for reset
  onSeek?: (time: number) => void; // NEW: Seek handler for timeline reset
  onReloadMediaItems?: () => void; // NEW: Callback to reload media from DB
}

const EditSegmentTabs: React.FC<EditSegmentTabsProps> = ({
  modalData,
  captions,
  setCaptions,
  getCaptionId,
  mediaItems,
  setMediaItems,
  onOpenAIVideoModal,
  onOpenUpscaleModal,
  projectId,
  addAtCurrentTime, // NEW
  currentTime, // NEW
  editMode = 'captions', // NEW - default to captions
  applyToAll = false, // NEW - default to false
  isConversionMode, // NEW
  targetAspectRatio, // NEW
  selectedMediaItem, // NEW
  onResetMediaItem, // NEW
  allMediaItemsFromDB = [], // NEW - preview data
  initialMediaItems = [], // NEW - for reset
  onSeek, // NEW - seek handler
  onReloadMediaItems, // NEW - reload callback
}) => {
  const { t } = useTranslation('videoEditor');
  const { t: tCaption } = useTranslation('captionEditor');
  const path = window.location.pathname;
  const user = useAuth().user;
  
  // Determine default tab based on workflow
  const isCreateContentWorkflow = !!path.includes('create-content');
  const isCaptionWorkflow = !!path.includes('caption');
  
  const getDefaultTab = () => {
    if (isCaptionWorkflow) return 'caption'; // Caption workflow shows only caption tab
    if (isCreateContentWorkflow) return 'media'; // Create content workflow defaults to media
    return 'caption'; // Default fallback
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [selectedPrompt, setSelectedPrompt] = useState<PromptProject | null>(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const { showNotification } = useNotification();

  // Auto-switch to media tab when a media item is selected
  useEffect(() => {
    if (modalData && modalData.isOpen && modalData.mediaItem) {
      setActiveTab('media');
    }
  }, [modalData]);

  // Handle prompt selection to show modal
  const handlePromptSelect = (prompt: string, project: PromptProject) => {
    setSelectedPrompt(project);
    setIsPromptModalOpen(true);
  };

  // Handle copying prompt to clipboard
  const handleCopyPrompt = async (prompt: string, project: PromptProject) => {
    try {
      await navigator.clipboard.writeText(prompt);
      showNotification({
        title: 'Copied!',
        message: 'Prompt copied to clipboard',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      showNotification({
        title: 'Copy failed',
        message: 'Could not copy to clipboard',
        type: 'error'
      });
    }
  };

  // Handle adding title at current time
  const handleAddTitleAtCurrentTime = () => {
    if (!currentTime && currentTime !== 0) return;
    
    const newTitle: Caption = {
      id: Date.now().toString(),
      start: currentTime,
      end: currentTime + 3, // Default 3 second duration
      text: tCaption('captionEditor.editSegment.defaultText.newTitle'),
      style: {
        fontSize: 48, // Larger font for titles
        color: '#ffffff',
        stroke: '#000000',
        strokeWidth: 2,
        fontFamily: 'Arial',
        yPosition: 40, // Position titles at top (40 * 0.5 = 20% from bottom)
      },
      itemType: 'titles' // Mark as title
    };
    
    setCaptions(prevCaptions => [...prevCaptions, newTitle]);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

 

  return (
    <div className={styles.editSegmentContainer}>
      <div className={styles.stack}>
        <div className={styles.tabList}>
          {!isCreateContentWorkflow && (
             <button
              className={`${styles.tab} ${activeTab === 'caption' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('caption')}
            >
              {editMode === 'captions' ? t('videoEditor.rightPanel.caption') : t('videoEditor.rightPanel.title')}
            </button>           
          )}
          {!isCaptionWorkflow && (
            <button
              className={`${styles.tab} ${activeTab === 'media' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('media')}
            >
              {t('videoEditor.rightPanel.media')}
            </button>
          )}
          {!isCaptionWorkflow && (
            <button
              className={`${styles.tab} ${activeTab === 'library' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('library')}
            >
              {t('videoEditor.rightPanel.library')}
            </button>
          )}
          {!isCaptionWorkflow && (
            <button
              className={`${styles.tab} ${activeTab === 'prompts' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('prompts')}
            >
              {t('videoEditor.rightPanel.prompts')}
            </button>
          )}
          {!isCaptionWorkflow && (
            <button
              className={`${styles.tab} ${activeTab === 'preview' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('preview')}
            >
              {t('videoEditor.rightPanel.preview')}
            </button>
          )}
        </div>
        <div className={styles.panel}>
          {activeTab === 'caption' && (
            <div>
              {modalData && modalData.isOpen && modalData.segment && captions.find(item => item.id === modalData.segment!.id) ? (
                <EditSegmentTooltip
                  segment={captions.find(item => item.id === modalData.segment!.id)!}
                  isOpen={modalData.isOpen}
                  onClose={modalData.onClose}
                  targetId={modalData.segment.id.toString()}
                  setCaptions={setCaptions}
                  editMode={editMode}
                  currentTime={currentTime}
                  applyToAll={applyToAll}
                />
              ) : (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: '#666',
                  background: '#1a1a1a',
                  borderRadius: '8px',
                  margin: '10px 0'
                }}>
                  <p style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '500' }}>
                    {editMode === 'captions' 
                      ? tCaption('captionEditor.rightPanel.noItemSelected.caption')
                      : tCaption('captionEditor.rightPanel.noItemSelected.title')
                    }
                  </p>
                  {editMode === 'titles' ? (
                    <>
                      <p style={{ margin: '0 0 15px', fontSize: '14px', opacity: 0.8 }}>
                        {tCaption('captionEditor.rightPanel.instructions.addTitle')}
                      </p>
                      <button
                        onClick={() => handleAddTitleAtCurrentTime()}
                        style={{
                          padding: '12px 24px',
                          background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          margin: '0 auto'
                        }}
                      >
                        ➕ {tCaption('captionEditor.rightPanel.buttons.addTitle')}
                      </button>
                    </>
                  ) : (
                    <p style={{ margin: '0', fontSize: '14px', opacity: 0.8 }}>
                      {tCaption('captionEditor.rightPanel.instructions.clickToEdit')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {activeTab === 'media' && (
            <div>
              {modalData && modalData.isOpen && modalData.mediaItem ? (
                <MediaTab
                  mediaMaker={mediaItems?.find(item => item.id === modalData.mediaItem!.id) || modalData.mediaItem!}
                  isOpen={modalData.isOpen}
                  onClose={modalData.onClose}
                  targetId={modalData.mediaItem.id.toString()}
                  setMediaItems={setMediaItems}
                  onOpenAIVideoModal={onOpenAIVideoModal}
                  onOpenUpscaleModal={onOpenUpscaleModal}
                  addAtCurrentTime={addAtCurrentTime}
                  currentTime={currentTime}
                  mediaItems={mediaItems}
                  isConversionMode={isConversionMode}
                  targetAspectRatio={targetAspectRatio}
                  selectedMediaItem={selectedMediaItem}
                  onResetMediaItem={onResetMediaItem}
                />
              ) : (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: '#666',
                  background: '#1a1a1a',
                  borderRadius: '8px',
                  margin: '10px 0'
                }}>
                  <p style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '500' }}>
                    {t('videoEditor.rightPanel.noMediaSelected')}
                  </p>
                  <p style={{ margin: '0', fontSize: '14px', opacity: 0.8 }}>
                    {t('videoEditor.rightPanel.noMediaMessage')}
                  </p>
                </div>
              )}
            </div>
          )}
          {activeTab === 'library' && (
            <div>
              {projectId ? (
                <LibraryTab
                  projectId={projectId}
                  setMediaItems={setMediaItems}
                  mediaItems={mediaItems}
                  addAtCurrentTime={addAtCurrentTime}
                  currentTime={currentTime}
                />
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <p>Library is available when working on a project.</p>
                </div>
              )}
            </div>
          )}
          {activeTab === 'prompts' && (
            <div className={styles.promptsPanel}>
              {/* Header text to indicate filtered prompts */}
              <div style={{ padding: '16px 16px 8px', borderBottom: '1px solid #e0e0e0', marginBottom: '16px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '600' }}>{t('videoEditor.prompts.title')}</h3>
                <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                  {projectId ? t('videoEditor.prompts.projectPrompts', { projectId }) : t('videoEditor.prompts.clickToView')}
                </p>
              </div>
              <PromptBrowser
                onPromptSelect={handlePromptSelect}
                onPromptCopy={handleCopyPrompt}
                height="350px"
                className={styles.videoEditorPromptBrowser}
                projectId={projectId} // Pass projectId to filter prompts by project
              />
            </div>
          )}
          {isAdminUser(user) && activeTab === 'preview' && (
            <div>
              {projectId ? (
                <PreviewTab
                  projectId={projectId}
                  onPreviewSelected={(option) => {
                    console.log('🎯 [PREVIEW SELECTED]', {
                      optionId: option.id,
                      optionName: option.name,
                      allMediaItemsCount: allMediaItemsFromDB.length,
                      currentMediaItemsCount: mediaItems.length
                    });
                    
                    // Use functional update to ensure we have latest state
                    setMediaItems(prevItems => {
                      console.log('🎯 [SET MEDIA ITEMS] Called with prevItems:', prevItems.length);
                      const newTimeline = handlePreviewSelectionSync(
                        option, 
                        allMediaItemsFromDB,
                        prevItems
                      );
                      console.log('🎯 [SET MEDIA ITEMS] New timeline length:', newTimeline.length);
                      return newTimeline;
                    });
                    
                    // Reset timeline to start after a brief delay to ensure state update
                    setTimeout(() => {
                      if (onSeek) {
                        onSeek(0);
                      }
                    }, 0);
                    
                    showNotification({
                      type: 'success',
                      title: 'Preview Applied',
                      message: `Applied ${option.name} preview to timeline`
                    });
                  }}
                  onClearPreview={() => {
                    // Use functional update for consistency
                    setMediaItems(() => [...initialMediaItems]);
                    
                    // Reset timeline to start after a brief delay
                    setTimeout(() => {
                      if (onSeek) {
                        onSeek(0);
                      }
                    }, 0);
                    
                    // showNotification({
                    //   type: 'info',
                    //   title: 'Preview Cleared',
                    //   message: 'Restored original timeline'
                    // });
                  }}
                  onPreviewGenerationComplete={() => {
                    // Reload media items from DB to get new preview segments
                    console.log('🔄 Reloading media items after preview generation');
                    if (onReloadMediaItems) {
                      onReloadMediaItems();
                    }
                  }}
                />
              ) : (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: '#666',
                  background: '#1a1a1a',
                  borderRadius: '8px',
                  margin: '10px 0'
                }}>
                  <p style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '500' }}>
                    {t('videoEditor.preview.available')}
                  </p>
                  <p style={{ margin: '0', fontSize: '14px', opacity: 0.8 }}>
                    {t('videoEditor.preview.saveFirst')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Prompt Modal Overlay */}
      {isPromptModalOpen && selectedPrompt && (
        <div className={styles.promptModalOverlay} onClick={() => setIsPromptModalOpen(false)}>
          <div className={styles.promptModalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.promptModalHeader}>
              <div className={styles.promptModalTitleContainer}>
                <h3 className={styles.promptModalTitle}>{selectedPrompt.title}</h3>
                <div className={styles.promptModalDate}>
                  {t('videoEditor.prompts.created')}: {formatDate(selectedPrompt.createdAt)}
                </div>
              </div>
              <button
                className={styles.promptModalClose}
                onClick={() => setIsPromptModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className={styles.promptModalContent}>
              {selectedPrompt.promptData?.generatedPrompt && (
                <div className={styles.promptDisplaySection}>
                  <div className={styles.promptSectionHeader}>
                    <h4 className={styles.promptSectionTitle}>{t('videoEditor.prompts.generatedPrompt')}</h4>
                    <button
                      className={styles.promptCopyButton}
                      onClick={() => handleCopyPrompt(selectedPrompt.promptData!.generatedPrompt, selectedPrompt)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      {t('videoEditor.prompts.copy')}
                    </button>
                  </div>
                  <div className={styles.promptText}>
                    {selectedPrompt.promptData.generatedPrompt}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(EditSegmentTabs);
